
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { MessageTranslatorService } from "../../services/message-translator-service";
import { ChangeDetectionStrategy, ChangeDetectorRef, HostListener, Component, OnDestroy, inject } from "@angular/core";
import { TasksConstants } from "../../shared/task-constacts";
import { TasksRootState, getMyTeamUsers, getAuthUser } from "../../store/index";
import { Store } from "@ngrx/store";
import { Broadcaster } from "../../../common/providers/broadcaster.service";

import { TeamUser, AuthUser } from "../../models";
import { TaskRepository } from "../../repository/task.repository";
import { getOnlineStatus } from "../../../reducers";
import { InviteUsersComponent } from "./invite-users.component";
import { ArchiveUsersComponent } from "./archive-users.components";
import { CommonUtil } from "../../../common/utils/common.utils";
import { ErrorService } from "../../../common/providers/error-service";
import { SuccessService } from "../../../common/providers/success-service";
import { ErrorType, SuccessType } from "../../shared/task-enum";
import { takeWhile } from "rxjs/operators";
import { VncLibraryService, VNCSpinnerComponent } from "@uxf/vnc-library";
import { MatButton } from "@angular/material/button";
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from "@angular/material/menu";
import { FormsModule } from "@angular/forms";
import { VNCLongPressDirective } from "../../../shared/directives/long-press.directive";
import { TranslatePipe } from "@ngx-translate/core";
import { TaskUserSearchPipe } from "../pipes/task-user-search.pipe";
import { MatIcon } from "@angular/material/icon";
import { MatList, MatListItem } from "@angular/material/list";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

@Component({
    selector: "vp-users-management",
    template: `
  <div class="task__dialog vp-users-management-dialog">
    <div class="task__dialog-header brand-color mat-elevation-z2">
      <div class="task__dialog-mobile-back-button">
        @if (selectedTeamUsersList.length === 0) {
          <button (click)="hide()" mat-button class="brand-color mat-button">
            <mat-icon>chevron_left</mat-icon>
          </button>
        }
        @if (selectedTeamUsersList.length > 0) {
          <button (click)="clearSelectedUser()" mat-butto class="brand-color mat-button">
            <mat-icon>clear</mat-icon>
          </button>
        }
      </div>
      @if (selectedTeamUsersList.length === 0) {
        <div class="task__dialog-desktop-close-button">
          <button (click)="hide()" class="brand-color mat-button">
            <mat-icon class="material-icons">clear</mat-icon>
          </button>
        </div>
      }
      @if (selectedTeamUsersList.length > 0) {
        <div class="desktop-clear-btn">
          <button (click)="clearSelectedUser()" class="brand-color mat-button">
            <mat-icon>chevron_left</mat-icon>
          </button>
        </div>
      }
      @if (selectedTeamUsersList.length === 0) {
        <div class="task__dialog-mobile-save-button button-icon" #bottomRightButton [matMenuTriggerFor]="archiveUserPopover">
          <button class="brand-color mat-button">
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>
      }
      @if (selectedTeamUsersList.length > 0) {
        <div class="task__dialog-mobile-save-button button-icon">
          @if (selectedTeamUsersList.length > 0) {
            <button class="brand-color mat-button" (click)="archiveUsers()">
              <mat-icon>archive</mat-icon>
            </button>
          }
        </div>
      }
      @if (selectedTeamUsersList.length === 0) {
        <h4>{{ 'USERS_MANAGEMENT' | translate}}</h4>
      }
      @if (selectedTeamUsersList.length > 0) {
        <h4 class="selected-list-header">{{ selectedTeamUsersList.length }}</h4>
      }
      @if (selectedTeamUsersList.length > 0) {
        <div class="desktop-header-btn">
          <button (click)="selectAllUsers()" class="brand-color mat-button select-all-btn">
            <mat-icon class="material-icons">done_all</mat-icon>
          </button>
          <button (click)="archiveUsers()" class="brand-color mat-button archive-btn">
            <mat-icon class="material-icons">archive</mat-icon>
          </button>
        </div>
      }
    </div>
    <div class="task__dialog-body">
      <div class="option-list">
        <mat-form-field class="filter-search-textfield">
          <mat-label>{{ 'SEARCH_USERS' | translate }}</mat-label>
          <input matInput [(ngModel)]="searchText">
        </mat-form-field>
        <mat-list>
          @for (teamUser of teamUserList  | vpTaskUserSearch : searchText; track teamUser) {
            <mat-list-item
              vpLongPress [timeout]="700" (onLongPress)="$event.stopPropagation();selectTeamUsers(teamUser)" (click)="onClickTeamUser(teamUser)" [class.collected-users]="teamUser.isSelected">
              @if (!teamUser.avatar) {
                <div  class="task-icon-avtar disable-select">{{ (teamUser) ? teamUser.firstLastNameCharacter : ('NO_NAME' | translate).charAt(0) }}</div>
              }
              @if (teamUser.avatar) {
                <div class="task-comment-avtar disable-select" [class.hide-avatar]="teamUser.isSelected">
                  <img img-cache [src] = "teamUser.avatar"/>
                </div>
              }
              @if (teamUser.isSelected) {
                <div class="task-icon-avtar task-done-avtar disable-select">
                  <mat-icon>done</mat-icon>
                </div>
              }
              <span class="username-place disable-select">{{ (teamUser) ? teamUser.name : ('NO_NAME' | translate) }}</span>
              @if (!isMobileView) {
                <mat-icon class="more-options" (click)="$event.stopPropagation();" [matMenuTriggerData]="{name: teamUser}" [matMenuTriggerFor]="moreMenu">more_vert</mat-icon>
              }
            </mat-list-item>
          }
        </mat-list>
      </div>
    </div>
    @if (selectedTeamUsersList.length === 0) {
      <button id="newUserButton" (click)="openInviteDialog()" mat-button class="add_list-button add-button-with-icon">
        <i class="material-icons disable-select">add</i>
      </button>
    }
    <mat-menu #archiveUserPopover="matMenu" class="team-user-item-menu" xPosition="after">
      <div class="archive-user-menu-popover">
        <mat-list>
          <mat-list-item class="menu-item" (click)="openArchiveUsersDialog()">
            <mat-icon>archive</mat-icon>
            {{'ARCHIVED_USERS' | translate}}
          </mat-list-item>
        </mat-list>
      </div>
    </mat-menu>
    <mat-menu #moreMenu="matMenu" class="team-user-item-menu" xPosition="after">
      <ng-template matMenuContent let-name="name">
        <div  mat-menu-item (click)="selectTeamUsers(name)">
          <mat-icon>check_circle</mat-icon>
          <span>{{ 'SELECT_USER' | translate }}</span>
        </div>
        <div  mat-menu-item (click)="selectAllUsers()">
          <mat-icon>done_all</mat-icon>
          <span>{{ 'SELECT_ALL_USERS' | translate }}</span>
        </div>
        <div  mat-menu-item (click)="archiveUser(name)">
          <mat-icon>archive</mat-icon>
          <span>{{ 'ARCHIVE_USER' | translate }}</span>
        </div>
      </ng-template>
    </mat-menu>
  </div>
  @if (isLoading) {
    <div class="vnctask-loading">
      <div class="indicator">
        @if (true) {
          <vnc-spinner [size]="'medium'"></vnc-spinner>
        }
      </div>
    </div>
  }
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatMenuTrigger, MatFormField, FormsModule, MatList, MatListItem, VNCLongPressDirective, MatMenu, MatMenuContent, MatMenuItem, VNCSpinnerComponent, TranslatePipe, TaskUserSearchPipe, MatInput, MatLabel]
})

export class UsersManagementComponent implements OnDestroy {
  private store = inject<Store<TasksRootState>>(Store);
  private broadcaster = inject(Broadcaster);
  private changerDetectorRef = inject(ChangeDetectorRef);
  private taskRepo = inject(TaskRepository);
  private dialog = inject<MatDialogRef<UsersManagementComponent>>(MatDialogRef);
  private matDialog = inject(MatDialog);
  private vncLibaryService = inject(VncLibraryService);
  private successService = inject(SuccessService);
  private errorService = inject(ErrorService);
  private messageTranslatorService = inject(MessageTranslatorService);

  selectedFilter: string;
  isAlive: boolean = true;
  taskConstants = TasksConstants;
  teamUserList: TeamUser[] = [];
  isOnline: boolean = false;
  searchText: string = "";
  isLongPressed: boolean =  false;
  selectedTeamUsersList: TeamUser[] = [];
  isLoading: boolean = false;
  selectedTeamUserCount: number;
  authUser: AuthUser;
  isMobileView: boolean = CommonUtil.isOnMobileDevice();
  constructor() {
    this.setupStore();
    this.handleUserManagementErrorMessages();
    this.handleUserManagementSuccessMessages();
    this.handleArchiveUserErrorMessages();
    this.handleArchiveUserSuccessMessages();
    this.broadcaster.on<any>("closeAllMdlDialogs").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.hide();
      });
    this.broadcaster.on<any>("hideUsersManagementDialog").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.hide();
      });
    if (this.isOnline) {
      this.isLoading = true;
    }
    this.taskRepo.syncMyTeamUsers();
    this.taskRepo.syncAuthUser();
  }

  setupStore() {
    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });
    this.store.select(getMyTeamUsers).pipe(takeWhile(() => this.isAlive)).subscribe((teamUsers) => {
      teamUsers = teamUsers.filter( teamUsers => teamUsers.archived === false);
      this.teamUserList = teamUsers;
      this.changerDetectorRef.markForCheck();
    });
    this.store.select(getAuthUser).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
      if (user) {
        this.authUser = user;
        this.changerDetectorRef.markForCheck();
      }
    });
  }

  handleUserManagementErrorMessages() {
    this.errorService.only(ErrorType.TeamUserListError).pipe(takeWhile(() => this.isAlive)).subscribe(error => {
      this.vncLibaryService.openSnackBar(error.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleUserManagementSuccessMessages() {
    this.successService.only(SuccessType.TeamUserListSuccess).pipe(takeWhile(() => this.isAlive)).subscribe(success => {
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleArchiveUserErrorMessages() {
    this.errorService.only(ErrorType.ArchiveUserError).pipe(takeWhile(() => this.isAlive)).subscribe(error => {
      this.vncLibaryService.openSnackBar(error.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleArchiveUserSuccessMessages() {
    this.successService.only(SuccessType.ArchiveUserSuccess).pipe(takeWhile(() => this.isAlive)).subscribe(success => {
      this.vncLibaryService.openSnackBar(this.selectedTeamUserCount + " " + success.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.selectedTeamUsersList = [];
      this.taskRepo.syncMyTeamUsers();
      this.taskRepo.syncAuthUser();
      this.changerDetectorRef.markForCheck();
    });
  }

  openInviteDialog() {
    this.matDialog.open(InviteUsersComponent, {
      minWidth: "250px",
      maxWidth: "calc(100% - 40px)",
      autoFocus: false,
      panelClass: "vp-invite-users-dialog"
    });
  }

  openArchiveUsersDialog() {
    this.matDialog.open(ArchiveUsersComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-archive-users-dialog"
    });
  }

  @HostListener("document:keydown.esc")
  hide() {
    this.dialog.close();
  }

  ngOnDestroy(): void {
    this.isAlive = false;
    this.changerDetectorRef.markForCheck();
  }

  selectTeamUsers(teamUser) {
    if (teamUser) {
      teamUser.isSelected = !teamUser.isSelected;
      this.isLongPressed = true;
      this.onSelectTeamUser(teamUser);
    }
  }

  onSelectTeamUser(teamUser) {
    if (teamUser.isSelected) {
      this.selectedTeamUsersList.push(teamUser);
    } else {
      for (let i = 0; i < this.selectedTeamUsersList.length; i++) {
        if (this.selectedTeamUsersList[i].id === teamUser.id) {
          this.selectedTeamUsersList.splice(i, 1);
        }
      }
    }
    this.changerDetectorRef.markForCheck();
  }

  selectAllUsers() {
    this.selectedTeamUsersList = [];
    for (let i = 0; i < this.teamUserList.length; i++) {
      this.teamUserList[i].isSelected = true;
      this.selectedTeamUsersList.push(this.teamUserList[i]);
    }
    this.changerDetectorRef.markForCheck();
  }

  onClickTeamUser(teamUser) {
    if (CommonUtil.isOnIOS() && this.isLongPressed) {
      this.isLongPressed = false;
      return;
    }
    if (this.selectedTeamUsersList.length > 0 ){
      teamUser.isSelected = !teamUser.isSelected;
      this.onSelectTeamUser(teamUser);
    }
  }

  archiveUser(teamUser) {
    if (this.isOnline) {
      if (teamUser) {
        let userIds: number[] = [];
        this.selectedTeamUserCount = 1;
        userIds.push(teamUser.id);
        this.isLoading = true;
        this.taskRepo.archiveUsers(userIds);
      }
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  archiveUsers() {
    if (this.isOnline) {
      if (this.selectedTeamUsersList.length > 0 ) {
        let userIds: number[] = [];
        this.selectedTeamUserCount = this.selectedTeamUsersList.length;
        this.selectedTeamUsersList.forEach(teamUser => {
          userIds.push(teamUser.id);
        });
        this.isLoading = true;
        this.taskRepo.archiveUsers(userIds);
      }
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  clearSelectedUser() {
    for (let i = 0; i < this.teamUserList.length; i++) {
      this.teamUserList[i].isSelected = false;
    }
    this.selectedTeamUsersList = [];
    this.changerDetectorRef.markForCheck();
  }
}
